import cookie from 'js-cookie';
import type { User } from 'types/User';

import type { View } from '~/routes/bolt/event/types';
import {
  getPageSeq,
  getPageViewId,
  getPreviousPageType,
  getPreviousPageViewId
} from '~/utils/navigationContext';
import { parseSearchString } from '~/utils/qs';

type EventData<T> = {
  clientIp: string | null;
  isBot: boolean;
  user: User;
  viewProperties?: T;
  viewType: string;
};

const SCHEMA_VERSION = 1;

class ViewEvent<T extends Record<string, unknown> = Record<string, unknown>> {
  private static lastSentId?: string;

  constructor(private data: EventData<T>) {}

  send() {
    if (this.data.isBot) return;

    const viewId = getPageViewId();

    if (!viewId || ViewEvent.lastSentId === viewId) return;

    ViewEvent.lastSentId = viewId;

    return fetch('/bolt/event', {
      body: JSON.stringify({ events: [this.buildEvent(viewId)] }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST'
    });
  }

  private normalizeViewType(pageType: string): string {
    if (pageType === 'homepage') return 'home';
    if (pageType === 'search') return 'plp';

    return pageType;
  }

  private getUtmParams() {
    const params = parseSearchString(window.location.search);

    return {
      utm_campaign: params.utm_campaign || undefined,
      utm_content: params.utm_content || undefined,
      utm_medium: params.utm_medium || undefined,
      utm_source: params.utm_source || undefined,
      utm_term: params.utm_term || undefined
    };
  }

  private buildEvent(viewId: string): View<T> {
    const previousViewId = getPreviousPageViewId();
    const previousPageType = getPreviousPageType();

    return {
      data: {
        ...this.getUtmParams(),
        client_ts: Date.now(),
        guest_id: this.data.user.guestId ?? cookie.get('clickRefUid'),
        platform_properties: {
          client_ip: this.data.clientIp ?? undefined,
          page_path: window.location.pathname,
          referrer: document.referrer || undefined,
          user_agent: navigator.userAgent
        },
        previous_view_id: previousViewId,
        previous_view_type:
          previousViewId && previousPageType
            ? this.normalizeViewType(previousPageType)
            : undefined,
        schema_version: SCHEMA_VERSION,
        view_id: viewId,
        view_properties: this.data.viewProperties,
        view_seq: getPageSeq(),
        view_type: this.normalizeViewType(this.data.viewType)
      },
      type: 'view'
    };
  }
}

export default ViewEvent;
